import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Plus, RefreshCw, Search, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const ACCOUNT_TYPES = [
  { value: 'GL', label: 'G/L Account' },
  { value: 'VENDOR', label: 'Vendors' },
  { value: 'CUSTOMER', label: 'Customers' },
  { value: 'ASSETS', label: 'Assets' },
  { value: 'MATERIAL', label: 'Material' },
];

const POSTING_KEYS = ['01', '11', '15', '21', '25', '31', '40', '50', '70', '75', '85', '86', '89', '96'];

const emptyForm = {
  document_type_id: '',
  name: '',
  description: '',
  account_type: 'GL',
  number_range_id: '',
  reversal_document_type: '',
  negative_posting_allowed: false,
  net_document_type: false,
  posting_key_debit: '',
  posting_key_credit: '',
};

const FIDocumentTypes = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('fi_document_type');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [docTypes, setDocTypes] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [numberRanges, setNumberRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState(emptyForm);

  // eslint-disable-next-line
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dt, nr] = await Promise.all([
        axios.get(`${backendUrl}/api/fi/document-types`),
        axios.get(`${backendUrl}/api/fi/number-ranges`),
      ]);
      setDocTypes(dt.data || []);
      // dedupe number_range_id (across company codes / fiscal years)
      const seen = new Set();
      const uniq = (nr.data || []).filter((r) => {
        if (seen.has(r.number_range_id)) return false;
        seen.add(r.number_range_id); return true;
      });
      setNumberRanges(uniq);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load FI configuration');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setFormData(emptyForm); setEditMode(false); setDialogOpen(true); };
  const openEdit = (row) => {
    setFormData({
      ...emptyForm,
      ...row,
      number_range_id: row.number_range_id || '',
      reversal_document_type: row.reversal_document_type || '',
      posting_key_debit: row.posting_key_debit || '',
      posting_key_credit: row.posting_key_credit || '',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.document_type_id || !formData.name) {
      toast.error('Document Type ID and Name are required'); return;
    }
    if (formData.document_type_id.length !== 2) {
      toast.error('Document Type ID must be exactly 2 characters'); return;
    }
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      payload.document_type_id = formData.document_type_id.toUpperCase();
      if (editMode) {
        const { document_type_id, ...rest } = payload;
        await axios.put(`${backendUrl}/api/fi/document-types/${document_type_id}`, rest);
        toast.success('Document type updated');
      } else {
        await axios.post(`${backendUrl}/api/fi/document-types`, payload);
        toast.success('Document type created');
      }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete FI Document Type '${id}'?`)) return;
    try {
      await axios.delete(`${backendUrl}/api/fi/document-types/${id}`);
      toast.success('Deleted'); loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleSeed = async () => {
    try {
      await axios.post(`${backendUrl}/api/fi/seed-defaults`);
      toast.success('Default FI document types & ranges seeded');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };

  const filtered = docTypes.filter((d) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      d.document_type_id?.toLowerCase().includes(q) ||
      d.name?.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="fi-document-types-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FileText className="w-7 h-7 text-orange-600" />
              FI Document Types
            </h1>
            <p className="text-gray-600 mt-1">SAP-standard finance document types (SA, KR, DR, AB, RE, …)</p>
          </div>
          <div className="flex gap-2">
            {writable && (
            <Button variant="outline" onClick={handleSeed} data-testid="seed-fi-defaults-btn">
              Seed Defaults
            </Button>
            )}
            <Button variant="outline" onClick={loadData} data-testid="refresh-fi-doc-btn">
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
            {writable && (
            <Button onClick={openCreate} data-testid="new-fi-doc-type-btn">
              <Plus className="w-4 h-4 mr-1" />New Document Type
            </Button>
            )}
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                placeholder="Search by ID, name, description..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="fi-doc-search-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{filtered.length} Document Type(s)</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Account Type</TableHead>
                    <TableHead>Number Range</TableHead>
                    <TableHead>Reversal</TableHead>
                    <TableHead>Posting Keys (Dr/Cr)</TableHead>
                    <TableHead>Flags</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.document_type_id} data-testid={`fi-doc-row-${row.document_type_id}`}>
                      <TableCell><Badge variant="outline">{row.document_type_id}</Badge></TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell><Badge>{row.account_type}</Badge></TableCell>
                      <TableCell>{row.number_range_id || '-'}</TableCell>
                      <TableCell>{row.reversal_document_type || '-'}</TableCell>
                      <TableCell>{row.posting_key_debit || '-'} / {row.posting_key_credit || '-'}</TableCell>
                      <TableCell className="text-xs">
                        {row.negative_posting_allowed && <Badge variant="secondary" className="mr-1">Neg</Badge>}
                        {row.net_document_type && <Badge variant="secondary">Net</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <RecordActions
                          doctype="fi_document_type"
                          idForTestId={`fi-doc-${row.document_type_id}`}
                          onView={() => setViewItem(row)}
                          onEdit={() => openEdit(row)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="fi-doc-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit' : 'New'} FI Document Type</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>Document Type ID *</Label>
                <Input
                  value={formData.document_type_id}
                  maxLength={2}
                  disabled={editMode}
                  onChange={(e) => setFormData({ ...formData, document_type_id: e.target.value.toUpperCase() })}
                  data-testid="fi-doc-id-input"
                />
              </div>
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="fi-doc-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Account Type *</Label>
                <Select value={formData.account_type} onValueChange={(v) => setFormData({ ...formData, account_type: v })}>
                  <SelectTrigger data-testid="fi-doc-account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Number Range</Label>
                <Select
                  value={formData.number_range_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, number_range_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="fi-doc-number-range-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {numberRanges.map((r) => (
                      <SelectItem key={r.number_range_id} value={r.number_range_id}>
                        {r.number_range_id} - {r.description || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reversal Doc Type</Label>
                <Select
                  value={formData.reversal_document_type || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, reversal_document_type: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="fi-doc-reversal-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {docTypes.filter(d => d.document_type_id !== formData.document_type_id).map((d) => (
                      <SelectItem key={d.document_type_id} value={d.document_type_id}>
                        {d.document_type_id} - {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Posting Key Debit</Label>
                <Select
                  value={formData.posting_key_debit || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, posting_key_debit: v === 'none' ? '' : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {POSTING_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Posting Key Credit</Label>
                <Select
                  value={formData.posting_key_credit || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, posting_key_credit: v === 'none' ? '' : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {POSTING_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Checkbox
                  checked={formData.negative_posting_allowed}
                  onCheckedChange={(v) => setFormData({ ...formData, negative_posting_allowed: !!v })}
                  data-testid="fi-doc-neg-posting-checkbox"
                />
                <Label>Allow Negative Posting</Label>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Checkbox
                  checked={formData.net_document_type}
                  onCheckedChange={(v) => setFormData({ ...formData, net_document_type: !!v })}
                  data-testid="fi-doc-net-checkbox"
                />
                <Label>Net Document Type</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="fi-doc-save-btn">{editMode ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RecordViewDialog
          open={!!viewItem}
          onOpenChange={(o) => { if (!o) setViewItem(null); }}
          title={`FI Document Type ${viewItem?.document_type_id || ''}`}
          record={viewItem}
        />
      </div>
    </Layout>
  );
};

export default FIDocumentTypes;
